import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DOC_NAV } from "../config/docNav.config";
import { resolveDocNavWithIcons } from "../config/docNavIcons";
import { isDocsLocalMode } from "../constants/docsEnv";
import { loadNavConfig } from "../../../utils/api/docs_apis";

const DocsConfigContext = createContext(null);

function getInitialNavState() {
  if (isDocsLocalMode()) {
    return {
      navStatus: "ready",
      docNav: resolveDocNavWithIcons(DOC_NAV),
    };
  }

  return {
    navStatus: "loading",
    docNav: [],
  };
}

export function DocsConfigProvider({ children }) {
  const [{ navStatus, docNav }, setNavState] = useState(getInitialNavState);

  useEffect(() => {
    if (isDocsLocalMode()) {
      return undefined;
    }

    let cancelled = false;

    const loadNav = async () => {
      setNavState({ navStatus: "loading", docNav: [] });
      const result = await loadNavConfig();

      if (cancelled) {
        return;
      }

      if (!result.ok) {
        setNavState({ navStatus: "error", docNav: [] });
        return;
      }

      setNavState({
        navStatus: "ready",
        docNav: resolveDocNavWithIcons(result.groups),
      });
    };

    loadNav();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      navStatus,
      docNav,
    }),
    [navStatus, docNav],
  );

  return (
    <DocsConfigContext.Provider value={value}>
      {children}
    </DocsConfigContext.Provider>
  );
}

export function useDocsConfig() {
  const context = useContext(DocsConfigContext);
  if (!context) {
    throw new Error("useDocsConfig must be used within DocsConfigProvider");
  }
  return context;
}
